import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Clock3,
  Filter,
  GraduationCap,
  Home,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  UserRound,
  Users,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import "./CoursesPage.css";
import {
  courseFallbackImage,
  formatCurrency,
  getCourseImage,
  getCoursePrice,
  getCourseSummary,
  getCourseTitle,
  listCourses,
} from "../api/courses";

function CourseCard({ course, isAr }) {
  const title = getCourseTitle(course, isAr);
  const summary = getCourseSummary(course, isAr);
  const price = getCoursePrice(course);
  const image = getCourseImage(course);
  const ratingValue = Number(course?.average_rating || course?.avg_rating || 4.9);
  const students = Number(course?.enrollments_count || course?.students_count || 120);

  const courseRef = String(course?.slug || course?.id || "").trim();
  const detailRef = courseRef && courseRef !== "html" ? courseRef : course?.id;

  return (
    <article className="courses-page-card">
      <Link to={`/courses/${detailRef}`} className="courses-page-card__media" aria-label={title}>
        <img src={image.src || courseFallbackImage} alt={title} className="courses-page-card__image" />

        <div className="courses-page-card__badges">
          <span className="courses-page-pill courses-page-pill--light">
            <GraduationCap size={14} className="me-1" />
            {course?.level || (isAr ? "جميع المستويات" : "All Levels")}
          </span>
          <span className="courses-page-pill courses-page-pill--price">
            {course?.is_free ? (isAr ? "مجاني" : "Free") : formatCurrency(price)}
          </span>
        </div>
      </Link>

      <div className="courses-page-card__content">
        <div className="courses-page-card__topline">
          <span className="d-flex align-items-center gap-1">
            <Sparkles size={14} className="text-success" />
            {course?.category || (isAr ? "كورس احترافي" : "Professional Course")}
          </span>
          <span className="d-flex align-items-center gap-1 text-warning font-bold">
            <Star size={14} fill="currentColor" />
            {ratingValue ? ratingValue.toFixed(1) : "4.9"}
          </span>
        </div>

        <h3 className="courses-page-card__title">
          <Link to={`/courses/${detailRef}`}>{title}</Link>
        </h3>
        <p className="courses-page-card__description">{summary}</p>

        <div className="courses-page-card__facts">
          <div>
            <Clock3 size={15} />
            <span>{course?.duration_text || (isAr ? "ساعات مرنة" : "Flexible Hours")}</span>
          </div>
          <div>
            <BookOpen size={15} />
            <span>
              {course?.lessons_count || 12} {isAr ? "درس" : "lessons"}
            </span>
          </div>
          <div>
            <Users size={15} />
            <span>
              {students} {isAr ? "طالب" : "students"}
            </span>
          </div>
        </div>

        <div className="courses-page-card__footer">
          <div className="courses-page-card__instructor">
            <UserRound size={16} className="text-success me-1" />
            <span>{course?.instructor_name || (isAr ? "موسى جروب" : "Mousa Group")}</span>
          </div>
          <Link to={`/courses/${detailRef}`} className="courses-page-card__button">
            <span>{isAr ? "عرض الكورس" : "View Course"}</span>
            <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function CoursesPage() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");
  const [rating, setRating] = useState(0);

  const CATEGORIES = [
    { id: "", labelAr: "جميع التخصصات", labelEn: "All Categories" },
    { id: "Programming", labelAr: "البرمجة والتطوير", labelEn: "Programming & Code" },
    { id: "Design", labelAr: "التصميم والواجهات", labelEn: "Design & UI/UX" },
    { id: "Marketing", labelAr: "التسويق الرقمي", labelEn: "Digital Marketing" },
    { id: "Business", labelAr: "الأعمال وإدارة المشاريع", labelEn: "Business & Management" },
    { id: "Tech", labelAr: "التكنولوجيا والذكاء الاصطناعي", labelEn: "Tech & AI" },
  ];

  const resetFilters = () => {
    setSearch("");
    setPriceRange([0, 10000]);
    setLevel("");
    setCategory("");
    setRating(0);
  };

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const list = await listCourses();
        if (active) setCourses(Array.isArray(list) ? list : []);
      } catch (err) {
        logger.error("Courses load error", err);
        if (active) setError(isAr ? "فشل تحميل قائمة الكورسات." : "Failed to load courses list.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isAr]);

  const filteredCourses = useMemo(() => {
    let result = courses;

    const query = search.trim().toLowerCase();
    if (query) {
      result = result.filter((course) => {
        const haystack = [
          course?.title_ar,
          course?.title_en,
          course?.short_description_ar,
          course?.short_description_en,
          course?.category,
          course?.instructor_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      });
    }

    result = result.filter((course) => {
      const price = course?.is_free ? 0 : Number(course?.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (level) {
      result = result.filter((course) => course?.level === level);
    }

    if (category) {
      result = result.filter((course) => course?.category === category);
    }

    if (rating > 0) {
      result = result.filter((course) => Number(course?.average_rating || course?.avg_rating || 0) >= rating);
    }

    return result;
  }, [courses, search, priceRange, level, category, rating]);

  const hasActiveFilters = Boolean(
    search.trim() || priceRange[0] !== 0 || priceRange[1] !== 10000 || level || category || rating > 0
  );

  return (
    <div className="courses-page-v2" dir={isAr ? "rtl" : "ltr"}>
      <div className="courses-page-shell">
        {/* Breadcrumb Navigation */}
        <nav className="courses-page-breadcrumb">
          <Link to="/">
            <Home size={16} />
            <span>{isAr ? "الرئيسية" : "Home"}</span>
          </Link>
          <span>/</span>
          <strong>{isAr ? "الكورسات والمسارات التقنية" : "Courses & Learning Tracks"}</strong>
        </nav>

        {/* Hero Section with Live Search */}
        <section className="courses-page-hero">
          <div className="courses-page-hero__copy">
            <div className="courses-page-hero__eyebrow">
              <Sparkles size={16} />
              <span>{isAr ? "مسارات تعليمية وتطبيقية معتمدة" : "Certified Hands-On Tech Courses"}</span>
            </div>

            <h1>
              {isAr
                ? "طوّر مهاراتك التقنية مع أفضل الكورسات والمسارات البرمجية"
                : "Upgrade Your Tech Skills with Expert Software Masterclasses"}
            </h1>

            <p>
              {isAr
                ? "استكشف دورات تدريبية عملية في تطوير المواقع والتطبيقات، التصميم، التسويق، والذكاء الاصطناعي مع متابعة مباشرة."
                : "Discover practical workshops and structured learning paths in full-stack coding, UI/UX design, marketing, and AI."}
            </p>

            {/* Live Search Panel */}
            <div className="courses-page-search">
              <Search size={20} className="text-muted flex-shrink-0" />
              <input
                type="text"
                placeholder={isAr ? "ابحث عن كورس، مدرب، أو تخصص برمجي..." : "Search course title, instructor, or tech stack..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Category Chips Bar */}
            <div className="courses-category-chips">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`courses-category-chip ${category === cat.id ? "is-active" : ""}`}
                  onClick={() => setCategory(cat.id)}
                >
                  {isAr ? cat.labelAr : cat.labelEn}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Filter Controls Bar */}
        <section className="courses-page-filters">
          <div className="courses-page-filters__title">
            <span>
              <Filter size={18} className="text-success" />
              {isAr ? "تصفية وفلترة الكورسات" : "Filter Courses"}
            </span>
            {hasActiveFilters ? (
              <button type="button" onClick={resetFilters}>
                <RotateCcw size={14} />
                <span>{isAr ? "إعادة تعيين الفلاتر" : "Reset Filters"}</span>
              </button>
            ) : null}
          </div>

          <div className="courses-page-filters__grid">
            <div className="courses-page-filter">
              <label>{isAr ? "نطاق السعر ($)" : "Price Range ($)"}</label>
              <div className="courses-page-filter__split">
                <input
                  type="number"
                  placeholder={isAr ? "أدنى" : "Min"}
                  value={priceRange[0] === 0 ? "" : priceRange[0]}
                  onChange={(e) => setPriceRange([Number(e.target.value || 0), priceRange[1]])}
                />
                <input
                  type="number"
                  placeholder={isAr ? "أقصى" : "Max"}
                  value={priceRange[1] === 10000 ? "" : priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value || 10000)])}
                />
              </div>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "المستوى" : "Level"}</label>
              <select value={level} onChange={(e) => setLevel(e.target.value)}>
                <option value="">{isAr ? "جميع المستويات" : "All Levels"}</option>
                <option value="Beginner">{isAr ? "مبتدئ" : "Beginner"}</option>
                <option value="Intermediate">{isAr ? "متوسط" : "Intermediate"}</option>
                <option value="Advanced">{isAr ? "متقدم" : "Advanced"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "التصنيف" : "Category"}</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">{isAr ? "جميع التخصصات" : "All Categories"}</option>
                <option value="Programming">{isAr ? "البرمجة" : "Programming"}</option>
                <option value="Design">{isAr ? "التصميم" : "Design"}</option>
                <option value="Business">{isAr ? "الأعمال" : "Business"}</option>
                <option value="Marketing">{isAr ? "التسويق" : "Marketing"}</option>
                <option value="Tech">{isAr ? "التكنولوجيا" : "Tech"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "التقييم الأدنى" : "Min Rating"}</label>
              <select value={rating} onChange={(e) => setRating(Number(e.target.value))}>
                <option value="0">{isAr ? "جميع التقييمات" : "All Ratings"}</option>
                <option value="4">★ 4.0+</option>
                <option value="4.5">★ 4.5+</option>
              </select>
            </div>
          </div>
        </section>

        {/* Section Header */}
        <div className="courses-page-section-head">
          <h2>{isAr ? "الكورسات والمسارات المتاحة" : "Available Courses"}</h2>
          <small className="text-muted font-bold">
            {filteredCourses.length} {isAr ? "كورس متاح" : "courses found"}
          </small>
        </div>

        {/* Content Body */}
        {loading ? (
          <div className="courses-page-empty">
            <div className="spinner-border text-success mb-3" style={{ width: "3rem", height: "3rem" }} />
            <p className="fw-semibold text-muted">{isAr ? "جاري تحميل الكورسات..." : "Loading courses..."}</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger rounded-4 p-4">{error}</div>
        ) : filteredCourses.length === 0 ? (
          <div className="courses-page-empty">
            <Search size={40} className="text-muted mb-2" />
            <h2>{isAr ? "لا توجد كورسات مطابقة لبحثك" : "No matching courses found"}</h2>
            <p className="text-muted">{isAr ? "جرّب تغيير كلمات البحث أو إعادة تعيين الفلاتر." : "Try adjusting your search terms or filter selections."}</p>
            <button type="button" onClick={resetFilters}>
              {isAr ? "إعادة تعيين الفلاتر" : "Reset Filters"}
            </button>
          </div>
        ) : (
          <div className="courses-page-grid">
            {filteredCourses.map((course) => (
              <CourseCard key={course.id} course={course} isAr={isAr} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
